import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Filter, GraduationCap, School } from 'lucide-react';

/**
 * Reusable CustomerDropdown Component (Fintech Stripe-like style)
 * - Styled in High Contrast: White Background, Black Text
 * - Opens with a beautiful shutter motion (smooth height transition)
 * - Supports searching by name, roll number, or family ID
 * - Includes class & section filters to manage thousands of students effortlessly
 */
export default function CustomerDropdown({ 
  options = [], 
  selectedValue = '', 
  onChange, 
  classes = [], 
  sections = [],
  placeholder = 'Select Student...' 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState('all');
  const [selectedSectionFilter, setSelectedSectionFilter] = useState('all');
  
  const containerRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeStudent = options.find(o => o.id === selectedValue);

  // Filter options dynamically based on search & class/section selectors
  const filteredOptions = options.filter(option => {
    const matchesSearch = 
      option.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.rollNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      option.familyId?.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesClass = selectedClassFilter === 'all' || option.classId === selectedClassFilter;
    const matchesSection = selectedSectionFilter === 'all' || option.sectionId === selectedSectionFilter;
    
    return matchesSearch && matchesClass && matchesSection;
  });

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-slate-200 hover:border-slate-300 rounded-xl px-4 py-3 flex items-center justify-between shadow-sm cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-slate-100"
      >
        <div className="flex flex-col items-left text-left">
          <span className="text-xs text-slate-400 font-bold uppercase tracking-wide">Selected Student</span>
          <span className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
            {activeStudent ? `${activeStudent.name} (Roll: ${activeStudent.rollNumber})` : placeholder}
          </span>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel with shutter motion height/opacity transition */}
      <div 
        className={`absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden transition-all duration-300 origin-top ${
          isOpen ? 'max-h-[480px] opacity-100 scale-100 visible' : 'max-h-0 opacity-0 scale-95 invisible'
        }`}
      >
        <div className="p-4 space-y-3 bg-white text-slate-900">
          
          {/* High-Fidelity Search Bar */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, roll number, or family ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-slate-400 focus:ring-0 outline-none text-xs text-slate-800 font-medium"
            />
          </div>

          {/* Filtering selectors: Class & Section */}
          <div className="grid grid-cols-2 gap-2 pb-2 border-b border-slate-100">
            <div className="relative flex items-center bg-slate-50 border border-slate-100 rounded-xl px-3 py-1.5 hover:bg-slate-100/60 transition-all">
              <GraduationCap className="w-3.5 h-3.5 text-slate-500 mr-2 shrink-0" />
              <select
                value={selectedClassFilter}
                onChange={(e) => {
                  setSelectedClassFilter(e.target.value);
                  setSelectedSectionFilter('all'); // reset section on class change
                }}
                className="w-full bg-transparent border-none p-0 text-[11px] font-bold text-slate-700 focus:ring-0 outline-none cursor-pointer"
              >
                <option value="all">All Classes</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
              </select>
            </div>

            <div className="relative flex items-center bg-slate-50 border border-slate-100 rounded-xl px-3 py-1.5 hover:bg-slate-100/60 transition-all">
              <School className="w-3.5 h-3.5 text-slate-500 mr-2 shrink-0" />
              <select
                value={selectedSectionFilter}
                onChange={(e) => setSelectedSectionFilter(e.target.value)}
                className="w-full bg-transparent border-none p-0 text-[11px] font-bold text-slate-700 focus:ring-0 outline-none cursor-pointer"
                disabled={selectedClassFilter === 'all'}
              >
                <option value="all">All Sections</option>
                {sections.filter(s => s.classId === selectedClassFilter).map(s => (
                  <option key={s.id} value={s.id}>{s.sectionName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Option listing viewport */}
          <div className="max-h-[220px] overflow-y-auto space-y-1 pr-1 scrollbar-thin">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optClass = classes.find(c => c.id === option.classId);
                const optSection = sections.find(s => s.id === option.sectionId);
                const isSelected = option.id === selectedValue;
                
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onChange(option.id);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 rounded-xl transition-all flex items-center justify-between text-xs ${
                      isSelected 
                        ? 'bg-slate-900 text-white font-semibold' 
                        : 'text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    <div>
                      <p className="font-bold text-sm leading-tight">{option.name}</p>
                      <p className={`text-[10px] mt-0.5 ${isSelected ? 'text-slate-400' : 'text-slate-400'}`}>
                        {optClass?.className || 'Class'} {optSection ? `- ${optSection.sectionName}` : ''} • Roll: {option.rollNumber}
                      </p>
                    </div>
                    {isSelected && (
                      <span className="w-1.5 h-1.5 bg-white rounded-full" />
                    )}
                  </button>
                );
              })
            ) : (
              <p className="text-center text-slate-400 text-xs italic py-6">No matching students found.</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
